import { INITIAL } from '@/utils/values/slider-filter-constants';

// The search has two modes. RESIDENCE ("Sök på annonser") finds apartments
// matching your criteria. WISH ("Sök på önskemål") is the inverse: you
// describe the apartment you have and it finds advertisers whose wishes it
// satisfies. The type decides the key in the API payload, the slider
// constants and how the slider values are sent (a range vs a single value).
export const searchTypes = {
  RESIDENCE: 'residence',
  WISH: 'wish'
};

// A filter is a wish search only when it says so. Anything else — including
// a filter built before `type` existed, such as an old bookmark or persisted
// state — is a residence search.
export function getSearchType(filter) {
  return filter && filter.type === searchTypes.WISH
    ? searchTypes.WISH
    : searchTypes.RESIDENCE;
}

// A saved coverage carries its filter under `residence` or `wish`.
export function getCoverageType(coverage) {
  return coverage && coverage.body && coverage.body[searchTypes.RESIDENCE]
    ? searchTypes.RESIDENCE
    : searchTypes.WISH;
}

export class SearchFilter {
  constructor(search) {
    if (!search) {
      search = {
        filter: {}
      };
    }

    this.type = getSearchType(search);
    const initialConstants = INITIAL[this.type.toUpperCase()];

    this.filter = {
      rooms: {
        min: initialConstants.ROOMS.MIN,
        max: initialConstants.ROOMS.MAX
      },
      sqm: {
        min: initialConstants.SQM.MIN,
        max: initialConstants.SQM.MAX
      },
      rent: {
        min: initialConstants.RENT.MIN,
        max: initialConstants.RENT.MAX
      }
    };

    this.filter.geolocations = [];
    this.filter.geolocationsSource = null;
    this.filter.freetext = null;

    this.filter.floor = null;

    this.filter.isBecomingCondo = false;
    this.filter.isCondo = false;
    this.filter.hasElevator = false;
    this.filter.hasFireplace = false;
    this.filter.hasBathtub = false;
    this.filter.hasBalcony = false;
    this.filter.hasImage = false;

    // No swap type checked by default — the search api treats an empty array
    // the same as an omitted one, so a fresh landing shows every listing.
    this.swapTypes = search.swapTypes || [];
    this.page = search.page || 1;
    this.pageSize = search.pageSize || 15;
    this.searchArea = search.searchArea || null;
    this.map = search.map || null;

    if (!search.filter) return;

    this.filter.geolocations = search.filter.geolocations || [];
    this.filter.geolocationsSource = search.filter.geolocationsSource || null;
    this.filter.freetext = search.filter.freetext || null;

    this.filter.sqm = getRangeOrValue(search.filter.sqm, this.filter.sqm);
    this.filter.rooms = getRangeOrValue(search.filter.rooms, this.filter.rooms);
    this.filter.rent = getRangeOrValue(search.filter.rent, this.filter.rent);

    this.filter.floor =
      search.filter.floor || search.filter.floor === 0
        ? search.filter.floor
        : null;

    this.filter.isBecomingCondo = search.filter.isBecomingCondo || false;
    this.filter.isCondo = search.filter.isCondo || false;
    this.filter.hasElevator = search.filter.hasElevator || false;
    this.filter.hasFireplace = search.filter.hasFireplace || false;
    this.filter.hasBathtub = search.filter.hasBathtub || false;
    this.filter.hasBalcony = search.filter.hasBalcony || false;
    this.filter.hasImage = search.filter.hasImage || false;
  }
}

export function getRangeOrValue(val, defaultVal) {
  if (typeof val === 'object' && (val.min || val.min === 0) && val.max) {
    return val;
  } else if (typeof val === 'object' && (val.min || val.min === 0)) {
    return { min: val.min, max: defaultVal.max };
  } else if (typeof val === 'number' && val !== null) {
    return {
      min: val,
      max: null
    };
  } else return defaultVal;
}

export function convertToSearchFilter(coverage) {
  const coverageCopy = JSON.parse(JSON.stringify(coverage));

  const filter = coverageCopy.body;
  filter.page = 1;

  const type = getCoverageType(coverageCopy);
  filter.type = type;
  filter.filter = coverageCopy.body[type];
  delete filter[type];

  return new SearchFilter(filter);
}

export function convertToCoverage(searchFilter) {
  searchFilter = JSON.parse(JSON.stringify(searchFilter));

  const type = getSearchType(searchFilter);

  if (type === searchTypes.RESIDENCE) {
    if (searchFilter.filter.sqm.max === INITIAL.RESIDENCE.SQM.MAX) {
      delete searchFilter.filter.sqm.max;
    }

    if (searchFilter.filter.rooms.max === INITIAL.RESIDENCE.ROOMS.MAX) {
      delete searchFilter.filter.rooms.max;
    }

    if (searchFilter.filter.rent.max === INITIAL.RESIDENCE.RENT.MAX) {
      delete searchFilter.filter.rent.max;
    }
  }

  const searchObject = {
    page: searchFilter.page || 1,
    pageSize: searchFilter.pageSize || 15,
    swapTypes: searchFilter.swapTypes,
    orderBy: searchFilter.orderBy || [],
    searchArea: searchFilter.searchArea
  };

  if (searchFilter.map) searchObject.map = searchFilter.map;
  if (searchFilter.fromMapInteraction)
    searchObject.fromMapInteraction = searchFilter.fromMapInteraction;

  const body = {
    ...searchFilter.filter
  };

  // Clean unused filter properties
  for (const key in body) {
    const value = body[key];
    if (!value && value !== 0) delete body[key];
  }

  if (body.geolocations && body.geolocations.length > 0)
    body.geolocations = body.geolocations.map(x => x.id);
  else if (body.geolocations && body.geolocations.length === 0)
    delete body.geolocations;

  searchObject[type] = type === searchTypes.WISH ? convertToWish(body) : body;

  return searchObject;
}

// A wish search sends each slider as a single value instead of a range: the
// max when there is one, otherwise the min.
export function convertToWish(filter) {
  filter = {
    ...filter
  };

  for (const key of Object.keys(filter)) {
    const val = filter[key];

    if (val && (val.max || val.max === 0)) filter[key] = val.max;
    else if (val && (val.min || val.min === 0)) filter[key] = val.min;
  }

  return filter;
}

export function getActiveFilterKeys(filter) {
  const defaultFilter = new SearchFilter({
    type: filter.type
  });

  const activeKeys = Object.keys(filter.filter).filter(
    key =>
      JSON.stringify(filter.filter[key]) !==
        JSON.stringify(defaultFilter.filter[key]) &&
      key !== 'geolocationsSource'
  );

  // Check if selected swap types is the same as default filter
  if (
    filter.swapTypes.length &&
    defaultFilter.swapTypes.toString() !== filter.swapTypes.toString()
  ) {
    activeKeys.push('swapTypes');
  }

  return activeKeys;
}
